/**
 * RegistrationProposalReview.tsx - the task «Eintragungsbestätigung prüfen» (Stefan P5 V2.0 step 25):
 * what the insurer's reply was read as (Ergebnis, Wirkungsdatum, Brokercode, ausgenommene Policen),
 * the registration it belongs to and why it came to a human, with the decisions «Übernehmen»,
 * «Korrigieren» and «Verwerfen».
 *
 * Without a usable reading (unklar, a failed parse) there is nothing to take over: the broker reads
 * the mail and records the answer through «Korrigieren» (eingetragen or abgelehnt) or discards it.
 * The decisions need mandate.eingangskontrolle; the reading itself is visible to every task reader.
 *
 * On top (review round 1): the mail the answer came in (Betreff, Absender, Eingang) and how it was
 * matched («Zuordnung: ...»), plus the parser's reason when it gave one. An implausible Wirkungsdatum
 * is never taken over as read: «Übernehmen» is not offered, the date goes in by hand (Korrigieren).
 */

import { useState, type ReactNode } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  REGISTRATION_REJECTION_REASON_LABEL,
  REGISTRATION_REJECTION_REASON_VALUES,
  REGISTRATION_REPLY_OUTCOME_LABEL,
} from '@brokly/shared';
import type { ApplyRegistrationProposalBody, RegistrationProposalDto, RegistrationRejectionReason } from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { dateTimeShortDe } from '../../lib/format';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useApplyRegistrationProposal, useRegistrationProposal } from './mandate-registration-queries';
import { REGISTRATION_DECISION_KEY } from './MandateRegistrationPanel';
import { registrationErrorText, reviewReasonText } from './registration-error-text';

const DECISION_LABEL: Record<string, string> = {
  uebernommen: 'Übernommen',
  korrigiert: 'Korrigiert',
  verworfen: 'Verworfen',
};

const INPUT = 'rounded border px-2 py-1 text-sm';

/** The review reason that forbids taking the reading over as is (p5s5-r1-call5). */
const IMPLAUSIBLE_DATE = 'effective_date_implausible';

/** A calendar date `YYYY-MM-DD` as dd.mm.yyyy (no time zone: it is a date, not a moment). */
function fmtDay(value: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1 text-sm">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

function CorrectForm({
  data,
  pending,
  onSubmit,
  onCancel,
}: {
  data: RegistrationProposalDto;
  pending: boolean;
  onSubmit: (body: ApplyRegistrationProposalBody) => void;
  onCancel: () => void;
}) {
  const p = data.proposal;
  const [outcome, setOutcome] = useState<'eingetragen' | 'abgelehnt'>(p?.outcome === 'abgelehnt' ? 'abgelehnt' : 'eingetragen');
  const [effectiveDate, setEffectiveDate] = useState(p?.effectiveDate ?? data.registration.requested_effective_date?.slice(0, 10) ?? '');
  const [brokerVuCode, setBrokerVuCode] = useState(p?.brokerVuCode ?? data.registration.broker_vu_code ?? '');
  const initialReason = REGISTRATION_REJECTION_REASON_VALUES.find((v) => v === p?.rejectionReasonCode) ?? '';
  const [reason, setReason] = useState<RegistrationRejectionReason | ''>(initialReason);
  const ready = outcome === 'eingetragen' || reason !== '';

  function submit() {
    const overrides: NonNullable<ApplyRegistrationProposalBody['overrides']> =
      outcome === 'eingetragen'
        ? {
            outcome,
            ...(effectiveDate !== '' && { effectiveDate }),
            ...(brokerVuCode.trim() !== '' && { brokerVuCode: brokerVuCode.trim() }),
          }
        : { outcome, ...(reason !== '' && { reasonCode: reason }) };
    onSubmit({ accept: true, overrides });
  }

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-md border bg-muted/30 p-2" data-testid="reg-proposal-correct-form">
      <div className="flex flex-wrap items-center gap-2">
        <select className={INPUT} aria-label="Ergebnis" value={outcome} onChange={(e) => setOutcome(e.target.value as 'eingetragen' | 'abgelehnt')}>
          <option value="eingetragen">{REGISTRATION_REPLY_OUTCOME_LABEL.eingetragen}</option>
          <option value="abgelehnt">{REGISTRATION_REPLY_OUTCOME_LABEL.abgelehnt}</option>
        </select>
        {outcome === 'eingetragen' ? (
          <>
            <input type="date" className={INPUT} aria-label="Wirkungsdatum" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
            <input className={INPUT} aria-label="Brokercode" placeholder="Brokercode" value={brokerVuCode} onChange={(e) => setBrokerVuCode(e.target.value)} />
          </>
        ) : (
          <select className={INPUT} aria-label="Grund" value={reason} onChange={(e) => setReason(e.target.value as RegistrationRejectionReason | '')}>
            <option value="">Grund wählen</option>
            {REGISTRATION_REJECTION_REASON_VALUES.map((v) => (
              <option key={v} value={v}>
                {REGISTRATION_REJECTION_REASON_LABEL[v]}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={pending || !ready} onClick={submit} data-testid="reg-proposal-correct-confirm">
          Korrigiert übernehmen
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
}

export function RegistrationProposalReview({ taskId }: { taskId: string }) {
  const query = useRegistrationProposal(taskId);
  const apply = useApplyRegistrationProposal(taskId);
  const { can } = useEffectivePermissions();
  const canDecide = can(REGISTRATION_DECISION_KEY);
  const [correcting, setCorrecting] = useState(false);

  if (query.isError) {
    return (
      <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
        <AlertTriangle className="size-4" />
        {registrationErrorText(query.error)}
      </p>
    );
  }
  if (query.data === undefined) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        <Loader2 className="size-4 animate-spin" /> Lädt …
      </p>
    );
  }

  const data = query.data;
  const { proposal, registration } = data;
  const numberOf = new Map(registration.policies.map((x) => [x.policy_id, x.policy_number ?? 'ohne Nummer']));
  const excluded = (proposal?.excludedPolicyIds ?? []).map((id) => numberOf.get(id) ?? id);
  const decided = data.reply.decision;
  const run = (body: ApplyRegistrationProposalBody) => apply.mutate(body, { onSuccess: () => setCorrecting(false) });

  const { mail, reply } = data;
  const dateImplausible = data.review_reasons.includes(IMPLAUSIBLE_DATE);

  return (
    <div className="rounded-md border border-border bg-background p-3" data-testid="reg-proposal">
      <div className="mb-2 border-b border-border pb-2" data-testid="reg-proposal-mail">
        <p className="text-sm font-medium">{mail.subject ?? '(kein Betreff)'}</p>
        <p className="text-xs text-muted-foreground">
          {mail.from ?? 'Absender unbekannt'}
          {mail.received_at ? ` · ${dateTimeShortDe(mail.received_at)}` : ''}
        </p>
        <p className="mt-1 text-xs text-muted-foreground" data-testid="reg-proposal-match">
          Zuordnung: {reply.match_method_label}
        </p>
      </div>
      <Line label="Versicherer">{registration.carrier_name ?? registration.carrier_uid}</Line>
      <Line label="Status der Meldung">{registration.status_label}</Line>
      {proposal ? (
        <>
          <Line label="Ergebnis">{REGISTRATION_REPLY_OUTCOME_LABEL[proposal.outcome]}</Line>
          <Line label="Wirkungsdatum">{fmtDay(proposal.effectiveDate) ?? '-'}</Line>
          <Line label="Brokercode">{proposal.brokerVuCode ?? '-'}</Line>
          <Line label="Ausgenommene Policen">
            <span data-testid="reg-proposal-excluded">{excluded.length > 0 ? excluded.join(', ') : 'keine'}</span>
          </Line>
        </>
      ) : (
        <p className="py-1 text-sm text-sem-decide">Keine Auswertung, bitte die Mail prüfen</p>
      )}
      {data.review_reasons.length > 0 && (
        <Line label="Zur Prüfung, weil">{data.review_reasons.map(reviewReasonText).join(', ')}</Line>
      )}
      {reply.reason_text && (
        <p className="py-1 text-sm text-muted-foreground" data-testid="reg-proposal-reason-text">
          {reply.reason_text}
        </p>
      )}

      {decided !== null ? (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="reg-proposal-decided">
          Bereits entschieden: {DECISION_LABEL[decided] ?? decided}
        </p>
      ) : (
        canDecide && (
          <>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {proposal && !dateImplausible && (
                <Button size="sm" disabled={apply.isPending} onClick={() => run({ accept: true })} data-testid="reg-proposal-accept">
                  Übernehmen
                </Button>
              )}
              <Button size="sm" variant="outline" disabled={apply.isPending} onClick={() => setCorrecting(true)} data-testid="reg-proposal-correct">
                Korrigieren
              </Button>
              <Button size="sm" variant="ghost" disabled={apply.isPending} onClick={() => run({ accept: false })} data-testid="reg-proposal-discard">
                Verwerfen
              </Button>
            </div>
            {dateImplausible && (
              <p className="mt-2 text-xs text-sem-decide" data-testid="reg-proposal-date-hint">
                Das Wirkungsdatum ist unplausibel und muss von Hand eingetragen werden (Korrigieren).
              </p>
            )}
            {correcting && <CorrectForm data={data} pending={apply.isPending} onSubmit={run} onCancel={() => setCorrecting(false)} />}
          </>
        )
      )}
      {apply.isError && (
        <p className="mt-2 flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="size-4" />
          {registrationErrorText(apply.error)}
        </p>
      )}
    </div>
  );
}
